import React, { useRef, useState } from "react";
import { Grid, Card, CardActionArea, CardContent, Typography, makeStyles } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    display: "flex",
    justifyContent: "center",
    overflowX: "auto",
    gap: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    "&::-webkit-scrollbar": {
      height: 8,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.grey[400],
      borderRadius: 4,
    }
  },
  card: {
    minWidth: 320,
    maxWidth: 320,
    height: "100%",
    position: "relative",
    border: "2px solid #000", 
    boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)",
    transition: "0.2s",
    backgroundColor: "#fff !important",
    color: "#000 !important", // Forzar texto negro
    "&:hover": {
      transform: "translate(-2px, -2px)",
      boxShadow: "6px 6px 0px 0px rgba(0,0,0,1)",
    }
  },
  cardSelected: {
    border: "2px solid #ccff00 !important", // Forzar verde neón
    backgroundColor: "#fbffeb !important", 
    boxShadow: "4px 4px 0px 0px #ccff00 !important",
  },
  videoContainer: {
    position: "relative",
    width: "100%",
    height: 380, 
    backgroundColor: "#000",
  },
  videoPreview: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  playOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    color: "#ccff00",
    fontSize: 64,
    opacity: 0.8,
    cursor: "pointer",
    transition: "0.2s",
    "&:hover": {
      transform: "translate(-50%, -50%) scale(1.1)",
      opacity: 1,
    }
  },
  badge: {
    position: "absolute",
    top: -12,
    left: 12,
    backgroundColor: "#fff",
    border: "2px solid #000",
    padding: "2px 8px",
    fontWeight: "bold",
    fontSize: "0.75rem",
    zIndex: 10,
  },
  tagNuevo: {
    position: "absolute",
    top: 4,
    right: 0,
    backgroundColor: "#2962ff",
    color: "#fff",
    border: "2px solid #000",
    padding: "2px 8px",
    fontWeight: "bold",
    fontSize: "0.75rem",
    transform: "rotate(5deg)",
    zIndex: 10,
  },
  checkIcon: {
    position: "absolute",
    bottom: 12,
    right: 12,
    color: "#ccff00",
    backgroundColor: "#000",
    borderRadius: "50%",
    fontSize: 32,
    border: "2px solid #000"
  }
}));

// Mock of local videos generated by Veo 3 that will live in public/assets/video_samples/
const templates = [
  {
    id: "podcast",
    title: "Podcast",
    description: "Dos personas hablando como en un podcast real (o tú solo, en monólogo). El formato que crea autoridad y confianza antes de vender: te compran sin conocerte.",
    videoUrl: "/assets/video_samples/podcast_sample.mp4",
    badge: "FICHA 01"
  },
  {
    id: "ugc",
    title: "UGC",
    description: "Una persona recomendando tu producto, como si fuera un cliente más — sin pagar actores ni influencers. El más versátil para testear rápido.",
    videoUrl: "/assets/video_samples/ugc_sample.mp4",
    badge: "FICHA 02"
  },
  {
    id: "dualcast",
    title: "DualCast",
    description: "Dos personas a la vez delante de cámara, hablando, interactuando entre ellas y con quien mira. Bien ejecutado, los resultados son una locura.",
    videoUrl: "/assets/video_samples/dualcast_sample.mp4",
    badge: "FICHA 03",
    tag: "NUEVO"
  },
  {
    id: "animado",
    title: "Ads Animados",
    description: "El más disruptivo. Visuales animados que explican el problema, la solución y el 'antes y después' con muchísima fuerza. Imposible de ignorar.",
    videoUrl: "/assets/video_samples/animado_sample.mp4",
    badge: "FICHA 04"
  },
  {
    id: "ugc_voz",
    title: "UGC con voz en off",
    description: "Imágenes tipo UGC con una voz superpuesta encima, sin poner a nadie frente a cámara. Perfecto para storytelling, demos rápidas y situaciones cotidianas.",
    videoUrl: "/assets/video_samples/ugc_voz_sample.mp4",
    badge: "FICHA 06"
  },
  {
    id: "trends",
    title: "Trends Virales",
    description: "Adapta a tu producto los trends que están arrasando AHORA en TikTok, Reels y Shorts — montándote en la ola justa, sin que se note forzado.",
    videoUrl: "/assets/video_samples/trends_sample.mp4",
    badge: "FICHA 07"
  }
];

const StepTemplates = ({ formData, updateFormData }) => {
  const classes = useStyles();
  const [activeVideoId, setActiveVideoId] = useState(null);
  const videoRefs = useRef({});

  const handlePlayPreview = (e, tplId) => {
    e.stopPropagation();
    
    // Pausar y silenciar a los demás
    Object.keys(videoRefs.current).forEach(key => {
      const vid = videoRefs.current[key];
      if (vid && key !== tplId) {
        vid.pause();
        vid.muted = true;
      }
    });

    const currentVid = videoRefs.current[tplId];
    if (currentVid) {
      if (currentVid.paused) {
        currentVid.muted = false;
        currentVid.currentTime = 0;
        currentVid.play();
        setActiveVideoId(tplId);
      } else {
        currentVid.pause();
        setActiveVideoId(null);
      }
    }
  };

  return (
    <div>
      <Typography variant="h6" gutterBottom>
        Elige el Formato del Video
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 32 }}>
        Selecciona la plantilla base. Observa la previsualización en video para entender el estilo visual que aplicará la IA.
      </Typography>
      
      <div className={classes.cardContainer}>
        {templates.map((tpl) => {
          const isSelected = formData.format === tpl.id;
          const isPlaying = activeVideoId === tpl.id;
          return (
            <div key={tpl.id} style={{ padding: "16px 4px", position: "relative" }}>
              {tpl.tag && <div className={classes.tagNuevo}>{tpl.tag}</div>}
              <Card 
                className={`${classes.card} ${isSelected ? classes.cardSelected : ""}`}
                onClick={() => updateFormData("format", tpl.id)}
                elevation={0}
              >
                <CardActionArea>
                  <div 
                    className={classes.videoContainer} 
                    onClick={(e) => handlePlayPreview(e, tpl.id)}
                  >
                    <video 
                      ref={el => videoRefs.current[tpl.id] = el}
                      className={classes.videoPreview} 
                      src={tpl.videoUrl} 
                      loop 
                      muted 
                      playsInline 
                    />
                    
                    {!isPlaying && (
                      <div className={classes.playOverlay}>
                        ▶
                      </div>
                    )}
                  </div>
                  
                  <CardContent style={{ padding: 24 }}>
                    <Typography variant="h6" style={{ fontWeight: 800, marginBottom: 8, letterSpacing: -0.5 }}>
                      {tpl.title}
                    </Typography>
                    <Typography variant="body2" style={{ color: "#333", lineHeight: 1.5, marginBottom: 16 }}>
                      {tpl.description}
                    </Typography>
                    <Typography variant="caption" style={{ fontWeight: "bold", borderBottom: "2px solid #000" }}>
                      Ver más ejemplos →
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StepTemplates;
